import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAccessToken } from '../hooks/useProfile';
import {
  flexRender,
  getCoreRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import Loader from './Loader';
import { useEffect, useMemo, useState } from 'react';
import IntakeForm from '../components/Intake';
import Modal from '../components/Modals/Modal';
import { RacePlan } from '../components/RacePlan';
import { dayjs } from '../lib/utils';
import {
  CheckIcon,
  PencilIcon,
  XIcon,
  MoreHorizontal,
  LoaderIcon,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import RaceModal, { RaceData } from '../components/Modals/RaceModal';
import AttachRaceModal from '../components/Modals/AttachRaceModal';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import PageHeader from '../components/PageHeader';
import {
  useAdminAttachRace,
  useAdminCreatePlan,
  useAdminImportWorkouts,
  useAdminUpsertRace,
} from '../hooks/mutations/useAdminPostActions';

const Admin = () => {
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const { data: accessToken } = useAccessToken();
  const navigate = useNavigate();
  const search = useSearch({ from: '/admin' });
  const activeTab = (search as any)?.tab || 'surveys';
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showRacePlanModal, setShowRacePlanModal] = useState(false);
  const [showRaceModal, setShowRaceModal] = useState(false);
  const [showAttachRaceModal, setShowAttachRaceModal] = useState(false);
  const [selectedRace, setSelectedRace] = useState<RaceData>();
  const [selectedSurvey, setSelectedSurvey] = useState<any>();
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [userWorkouts, setUserWorkouts] = useState<any>();
  const importWorkoutsMutation = useAdminImportWorkouts();
  const createPlanMutation = useAdminCreatePlan();
  const upsertRaceMutation = useAdminUpsertRace();
  const attachRaceMutation = useAdminAttachRace();
  const handleViewResponses = (data: any) => {
    setSelectedSurvey({
      ...data,
      survey_responses: JSON.parse(data.survey_responses),
    });
    setShowSurveyModal(true);
  };

  const handleViewPlan = async (data: any) => {
    const userWorkouts = await fetchUserWorkouts(
      data.user_id,
      accessToken || '',
    );
    setUserWorkouts(userWorkouts);
    setSelectedPlan({
      id: data.id,
      summary: data.summary,
      goal_time: data.goal_time,
      user_id: data.user_id,
      user_name: data.user_name,
      user_email: data.user_email,
      connection_id: data.connection_id,
      created_at: data.created_at,
      training_plan: data.training_plan,
      race_gpx_file_url: data.race_gpx_file_url,
      ...data,
    });
    setShowRacePlanModal(true);
  };

  const handleCloseModal = () => {
    setSelectedPlan(null);
    setSelectedSurvey(null);
    setShowSurveyModal(false);
  };

  const { data: surveys, isLoading: isLoadingSurveys } = useQuery({
    queryKey: ['racePlanSurveys'],
    queryFn: async () => {
      if (!accessToken) {
        navigate({ to: '/' });
        return;
      }
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/race-plans/surveys`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      return data.surveys;
    },
    refetchInterval: 10000,
  });

  const { data: races, isLoading: isLoadingRaces } = useQuery({
    queryKey: ['races'],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      return data.races || [];
    },
    refetchInterval: 10000,
  });

  const { data: allPlans, isLoading: isLoadingAllPlans } = useQuery({
    queryKey: ['allRacePlans'],
    queryFn: async () => {
      if (!accessToken) {
        navigate({ to: '/' });
        return;
      }
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/race-plans/all`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      return data.plans;
    },
    refetchInterval: 10000,
  });

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/users`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      return data.users;
    },
  });

  const handleImportWorkouts = async (userId: number) => {
    return importWorkoutsMutation.mutateAsync(userId);
  };

  const handleDeleteSurvey = async (id: number) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/delete-survey`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ id }),
      },
    );
    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ['racePlanSurveys'] });
    }
    return response;
  };

  const handleCreatePlan = async (id: number) => {
    return createPlanMutation.mutateAsync(id);
  };

  const handleDeletePlan = async (id: number) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/delete-plan`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      },
    );
    const data = await response.json();
    if (data) {
      queryClient.invalidateQueries({ queryKey: ['racePlanSurveys'] });
      queryClient.invalidateQueries({ queryKey: ['allRacePlans'] });
    }
    return;
  };

  const handleDeleteUser = async (id: number) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/users/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
    return response;
  };

  const handleUpdateUser = async (id: number, user: any) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/users/${id}/update`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(user),
      },
    );
    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
    return response;
  };

  const handleUpdateRace = async (race: RaceData): Promise<void> => {
    await upsertRaceMutation.mutateAsync(race);
  };

  const handleDeleteRace = async (id: number) => {
    const response = await fetch(
      `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ['races'] });
    }
    return response;
  };

  const handleAttachRace = async (
    surveyId: number,
    raceId: number,
  ): Promise<void> => {
    await attachRaceMutation.mutateAsync({ surveyId, raceId });
    setShowAttachRaceModal(false);
  };

  const surveysTable = useReactTable({
    data: isLoadingSurveys ? [] : surveys || [],
    columns: [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'race.name',
        header: 'Race Name',
        cell: (data: any) => {
          return (
            <button
              className="text-brand-cobalt underline"
              onClick={() => {
                setSelectedRace(data.row.original.race);
                setShowRaceModal(true);
              }}
            >
              {data.getValue() || 'No Race Selected'}
            </button>
          );
        },
      },
      {
        accessorKey: 'workouts_imported',
        header: '# Workouts',
        cell: (d: any) => {
          return (
            <UserWorkoutsCell
              userId={d.row.original.user_id}
              accessToken={accessToken || ''}
            />
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        cell: (data: any) => new Date(data.getValue()).toLocaleDateString(),
      },
      {
        accessorKey: 'race_plan_id',
        header: 'Plan Status',
        cell: (data: any) => {
          return data.getValue() ? 'Generated' : 'Not Generated';
        },
      },
      {
        accessorKey: 'survey_responses',
        header: 'Survey Responses',
        cell: (data: any) => {
          return (
            <button
              className="text-brand-cobalt underline"
              onClick={() => handleViewResponses(data.row.original)}
            >
              View Responses
            </button>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: (data: any) => {
          return (
            <div className="flex justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      if (
                        window.confirm(
                          data.row.original.race_plan_id
                            ? 'This survey already generated a plan. Generate a new plan from this survey and replace the linked plan?'
                            : 'Are you sure you want to create this plan?',
                        )
                      ) {
                        handleCreatePlan(data.row.original.id);
                      }
                    }}
                  >
                    {data.row.original.race_plan_id
                      ? 'Generate New Plan'
                      : 'Create Plan'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedSurvey(data.row.original);
                      setShowAttachRaceModal(true);
                    }}
                  >
                    Attach Race
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      window.confirm(
                        'Are you sure you want to delete this survey?',
                      ) && handleDeleteSurvey(data.row.original.id)
                    }
                    className="text-red-600"
                  >
                    Delete Survey
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  const allPlansTable = useReactTable({
    data: isLoadingAllPlans ? [] : allPlans || [],
    columns: [
      {
        accessorKey: 'user_name',
        header: 'Name',
      },
      {
        accessorKey: 'user_email',
        header: 'Email',
      },
      {
        accessorKey: 'race.name',
        header: 'Race Name',
        cell: (data: any) => {
          return (
            <button
              className="text-brand-cobalt underline"
              onClick={() => {
                setSelectedRace(data.row.original.race);
                setShowRaceModal(true);
              }}
            >
              {data.getValue() || 'No Race Selected'}
            </button>
          );
        },
      },
      {
        accessorKey: 'summary',
        header: 'Summary',
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        cell: (data: any) => new Date(data.getValue()).toLocaleDateString(),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: (data: any) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleViewPlan(data.row.original)}
                >
                  View Plan
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() =>
                    window.confirm(
                      'Are you sure you want to delete this plan?',
                    ) && handleDeletePlan(data.row.original.id)
                  }
                  className="text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  const racesTable = useReactTable({
    data: isLoadingRaces ? [] : races || [],
    columns: [
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'date',
        header: 'Date',
        cell: (data: any) => dayjs(data.getValue()).format('MM/DD/YYYY'),
      },
      { accessorKey: 'distance', header: 'Distance' },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: (data: any) => {
          if (!data.getValue()) {
            return 'N/A';
          }
          const raceUrl = data.getValue();
          return (
            <a
              className="text-brand-cobalt underline"
              href={raceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Race
            </a>
          );
        },
      },
      {
        accessorKey: 'gpx_file_url',
        header: 'Map',
        cell: (data: any) => {
          if (!data.getValue()) {
            return 'N/A';
          }
          const url = data.getValue();
          return (
            <button
              className="text-brand-cobalt underline"
              onClick={() => {
                setSelectedRace(data.row.original);
                setShowRaceModal(true);
              }}
            >
              View Map
            </button>
          );
        },
      },
      {
        accessorKey: 'athletes',
        header: 'Athletes',
        cell: (data: any) => {
          return (
            <AthletesCell
              raceId={data.row.original.id}
              accessToken={accessToken || ''}
            />
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: (data: any) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedRace(data.row.original);
                    setShowRaceModal(true);
                  }}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() =>
                    window.confirm(
                      'Are you sure you want to delete this race?',
                    ) && handleDeleteRace(data.row.original.id)
                  }
                  className="text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  const usersTable = useReactTable({
    data: isLoadingUsers ? [] : users || [],
    columns: [
      {
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (data: any) => {
          const [isEditing, setIsEditing] = useState(false);
          const [name, setName] = useState(data.getValue());

          const handleSave = async () => {
            await handleUpdateUser(data.row.original.id, { name });
            setIsEditing(false);
          };

          if (isEditing) {
            return (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="border rounded px-2 py-1"
                />
                <button onClick={() => handleSave()}>
                  <CheckIcon className="w-4 h-4 text-brand-green" />
                </button>
                <button onClick={() => setIsEditing(false)}>
                  <XIcon className="w-4 h-4 text-red-500" />
                </button>
              </div>
            );
          }

          return (
            <div className="flex items-center gap-2">
              <span>{data.getValue()}</span>
              <button onClick={() => setIsEditing(true)}>
                <PencilIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'titleEnabled',
        header: 'Title',
        cell: (data: any) => (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={data.getValue()}
              className="w-4 h-4"
              onChange={() =>
                handleUpdateUser(data.row.original.id, {
                  titleEnabled: !data.getValue(),
                })
              }
            />
          </div>
        ),
      },
      {
        accessorKey: 'descriptionEnabled',
        header: 'Description',
        cell: (data: any) => (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={data.getValue()}
              className="w-4 h-4"
              onChange={() =>
                handleUpdateUser(data.row.original.id, {
                  descriptionEnabled: !data.getValue(),
                })
              }
            />
          </div>
        ),
      },
      {
        accessorKey: 'workouts',
        header: 'Workouts',
        cell: (data: any) => (
          <UserWorkoutsCell
            userId={data.row.original.id}
            accessToken={accessToken || ''}
          />
        ),
      },
      {
        accessorKey: 'language',
        header: 'Language',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (data: any) => dayjs(data.getValue()).format('MM/DD/YYYY HH:mm'),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: (data: any) => dayjs(data.getValue()).format('MM/DD/YYYY HH:mm'),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: (data: any) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to import workouts for this user?',
                      )
                    ) {
                      handleImportWorkouts(data.row.original.id);
                    }
                  }}
                >
                  Import Workouts
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to email this user?',
                      )
                    ) {
                      // handleEmail(data.row.original.id)
                    }
                  }}
                >
                  Email User
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this user?',
                      )
                    ) {
                      handleDeleteUser(data.row.original.id);
                    }
                  }}
                  className="text-red-600"
                >
                  Delete User
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoadingSurveys || isLoadingAllPlans) {
    return <Loader />;
  }

  // Redirect if not admin
  if (user?.email !== 'dfieds913@gmail.com') {
    navigate({ to: '/' });
    return null;
  }

  const handleTabChange = (tab: string) => {
    navigate({
      to: '/admin',
      search: { tab },
    });
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <PageHeader
        eyebrow="Operations"
        title="Admin dashboard"
        description="Review incoming plans, users, and races from one workspace."
        icon={ShieldCheck}
      />
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="surveys">Surveys</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="races">Races</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="surveys">
          <div className="surface-card p-4 sm:p-6">
            <h2 className="text-xl font-semibold mb-2">Race Plan Surveys</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Surveys are kept after a plan is generated. Use Generate New Plan
              on a generated survey to create a replacement plan from those
              saved responses.
            </p>
            <AdminDataTable table={surveysTable} />
          </div>
        </TabsContent>

        <TabsContent value="plans">
          <div className="surface-card p-4 sm:p-6">
            <h2 className="text-xl font-semibold mb-4">All Plans</h2>
            <AdminDataTable table={allPlansTable} />
          </div>
        </TabsContent>

        <TabsContent value="races">
          <div className="surface-card p-4 sm:p-6">
            <div className="flex flex-row justify-between items-center">
              <h2 className="text-xl font-semibold mb-4">Races</h2>
              <button
                className="bg-brand-volt text-brand-midnight px-2 py-1 rounded"
                onClick={() => setShowRaceModal(true)}
              >
                Add Race
              </button>
            </div>
            <AdminDataTable table={racesTable} />
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="surface-card p-4 sm:p-6">
            <h2 className="text-xl font-semibold mb-4">Users</h2>
            <AdminDataTable table={usersTable} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Race Modal */}
      {showRaceModal ? (
        <RaceModal
          show={showRaceModal}
          onClose={() => setShowRaceModal(false)}
          onSubmit={handleUpdateRace}
          existingRace={selectedRace}
          disabled={false}
        />
      ) : null}
      {/* Race Plan Modal */}
      {selectedPlan ? (
        <Modal onClose={handleCloseModal} show={showRacePlanModal}>
          <RacePlan
            racePlan={selectedPlan}
            completedWorkouts={userWorkouts || []}
          />
        </Modal>
      ) : null}

      {/* Survey Modal */}
      {selectedSurvey && !showAttachRaceModal ? (
        <Modal
          onClose={handleCloseModal}
          show={showSurveyModal && selectedSurvey}
        >
          <IntakeForm data={selectedSurvey} />
        </Modal>
      ) : null}

      {/* Attach Race Modal */}
      {selectedSurvey && showAttachRaceModal ? (
        <AttachRaceModal
          show={showAttachRaceModal}
          onClose={() => {
            setShowAttachRaceModal(false);
            setSelectedSurvey(null);
          }}
          onSubmit={handleAttachRace}
          onRaceAdd={handleUpdateRace}
          races={races || []}
          survey={selectedSurvey}
        />
      ) : null}
    </div>
  );
};

const ADMIN_PAGE_SIZES = [10, 25, 50] as const;

const getSearchableText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return Object.values(value).map(getSearchableText).join(' ');
  }

  return String(value);
};

const AdminDataTable = ({ table }: { table: Table<any> }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(ADMIN_PAGE_SIZES[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const rows = table.getRowModel().rows;
  const filteredRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();

    if (!normalizedQuery) {
      return rows;
    }

    return rows.filter((row) =>
      getSearchableText(row.original)
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    );
  }, [rows, searchQuery]);
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = filteredRows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  useEffect(() => {
    setPage(0);
  }, [searchQuery, pageSize]);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <label className="flex flex-1 flex-col gap-1 text-sm font-medium">
          Search
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search this table..."
            className="w-full rounded-md border bg-background px-3 py-2 font-normal sm:max-w-sm"
          />
        </label>
        <label className="flex items-center gap-2 text-sm font-medium">
          Rows per page
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="rounded-md border bg-background px-3 py-2 font-normal"
          >
            {ADMIN_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-muted text-muted-foreground">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="whitespace-nowrap border-b px-3 py-3 text-left font-semibold"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.id} className="transition-colors hover:bg-muted/50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap border-b px-3 py-3 last:border-b-0"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 md:hidden">
        {visibleRows.map((row) => (
          <article key={row.id} className="rounded-lg border bg-background p-4">
            <dl className="grid gap-3">
              {row.getVisibleCells().map((cell) => (
                <div
                  key={cell.id}
                  className="grid grid-cols-[minmax(6rem,0.4fr)_minmax(0,1fr)] items-start gap-3 border-b pb-3 last:border-0 last:pb-0"
                >
                  <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {String(cell.column.columnDef.header || '')}
                  </dt>
                  <dd className="min-w-0 break-words text-right">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      {filteredRows.length === 0 ? (
        <p className="rounded-md border p-6 text-center text-muted-foreground">
          {searchQuery.trim()
            ? 'No matching records found.'
            : 'No records found.'}
        </p>
      ) : null}

      {filteredRows.length > 0 ? (
        <nav
          aria-label="Table pagination"
          className="mt-4 flex items-center justify-between gap-3"
        >
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage === 0}
            onClick={() => setPage(Math.max(0, currentPage - 1))}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {pageCount} ({filteredRows.length}{' '}
            {filteredRows.length === 1 ? 'row' : 'rows'})
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage === pageCount - 1}
            onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </nav>
      ) : null}
    </div>
  );
};

export default Admin;

const fetchUserWorkouts = async (userId: number, accessToken: string) => {
  if (!userId) {
    return [];
  }
  const response = await fetch(
    `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/users/${userId}/workouts`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  const data = await response.json();
  return data.workouts;
};

const UserWorkoutsCell = ({
  userId,
  accessToken,
}: {
  userId: number;
  accessToken: string;
}) => {
  const {
    data: userWorkouts,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['userWorkouts', userId],
    queryFn: () => fetchUserWorkouts(userId, accessToken),
  });

  if (isLoading) return <Loader />;
  if (isError) return <span>Error: {error.message}</span>;

  return <span>{userWorkouts?.length || 0}</span>;
};

const AthletesCell = ({
  raceId,
  accessToken,
}: {
  raceId: number;
  accessToken: string;
}) => {
  const {
    data: athletes,
    isLoading: isLoadingAthletes,
    isError,
    error,
  } = useQuery({
    queryKey: ['athletes', raceId],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races/${raceId}/athletes`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const data = await response.json();
      return data.athletes;
    },
  });
  if (isLoadingAthletes)
    return (
      <span>
        <LoaderIcon className="animate-spin" size={18} />
      </span>
    );
  if (isError) return <span>Error: {error.message}</span>;
  return <span>{athletes?.length || 0}</span>;
};
